import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'

import {
  createEmailGroup,
  loadEmailGroups,
  updateEmailGroup,
} from '@disco/state-management/actions/EmailGroupActions'
import { updateBusinessSettings } from '@disco/state-management/actions/UserActions'
import { getEmailGroup } from '@disco/state-management/selectors/emailGroups'

import type { RecipientList } from 'types/list'
import type { State } from 'types/redux'

import BusinessAddressField from 'components/common/business-address-field'
import { Box } from 'components/ds'
import { Spinner } from 'components/ui'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Heading from 'components/ui/heading'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'

import type { RootState } from 'store/configureStore'

type OwnProps = {
  params: {
    listId?: string
  }
}
type DraftJsDoc = Record<string, any>

function mapStateToProps(state: RootState, props: OwnProps): any {
  let listId
  let list = null

  if (props.params.listId) {
    listId = parseInt(props.params.listId)
  }

  if (listId) {
    list = getEmailGroup(state, {
      listId: listId,
    })
  }

  return {
    list,
    user: state.user,
    emailGroups: state.emailGroups,
  }
}

const actions = {
  loadEmailGroups,
  updateBusinessSettings,
  updateEmailGroup,
  createEmailGroup,
}
type ValidationProps = {
  clearValidations: () => unknown
  validate: (...args: Array<any>) => any
  isValid: (key?: string) => boolean
  getValidationMessages: (...args: Array<any>) => any
}

type OwnState = {
  name: string
  sender_name: string
  sender_email: string
  business_address: DraftJsDoc | null | undefined
  business_address_mjml: string | null | undefined
  business_address_text: string | null | undefined
}

class MailingListForm extends React.Component<
  {
    formTitle: string
    user: State['user']
    list: RecipientList | null | undefined
    emailGroups: State['emailGroups']
    navigate: NavigateFunction
  } & ValidationProps &
    typeof actions,
  OwnState
> {
  state: OwnState = {
    name: this.props.list ? this.props.list.name : '',
    sender_name:
      (this.props.list ? this.props.list.sender_name : '') ||
      this.props.user.first_name,
    sender_email:
      (this.props.list ? this.props.list.sender_email : '') ||
      this.props.user.email,
    business_address:
      this.props.user.business && this.props.user.business.business_address,
    business_address_mjml:
      this.props.user.business &&
      this.props.user.business.business_address_mjml,
    business_address_text: '_', // '_' - filler, only for validation
  }
  getValidatorData = () => ({
    name: this.state.name,
    sender_name: this.state.sender_name,
    sender_email: this.state.sender_email,
    business_address_text: (this.state.business_address_text || '').trim(),
  })
  validatorTypes = strategy.createSchema(
    {
      name: 'required|max:100',
      sender_name: 'required',
      sender_email: 'required',
      business_address_text: 'required',
    },
    {
      'required.name': 'Please enter a name',
      'required.sender_name': 'Please enter a sender name',
      'required.sender_email': 'Please enter a sender address',
      'required.business_address_text': 'Please enter a business address',
    },
  )

  componentDidMount() {
    this.props.loadEmailGroups()
  }

  _handleBusinessAddressChange = (val, valHtml, valMjml, valText) => {
    this.props.clearValidations()
    this.setState({
      business_address: val,
      business_address_mjml: valMjml,
      business_address_text: valText,
    })
  }
  _handleSubmit = async () => {
    this.props.validate(error => {
      if (!error) this._submitToServer()
    })
  }
  _submitToServer = async () => {
    const attrs: Partial<RecipientList> = {
      name: this.state.name,
      sender_name: this.state.sender_name,
      sender_email: this.state.sender_email,
    }

    if (
      this.props.user.permissions?.includes('can_change_business_settings') &&
      this.props.user.business?.business_address_mjml !==
        this.state.business_address_mjml
    ) {
      await this.props.updateBusinessSettings(
        {
          business_address: this.state.business_address,
          // careful: html (business_address_mjml) is stored as "mjml"
          // generally this is not correct: html has to be wrapped in mjml tag
          business_address_mjml: this.state.business_address_mjml,
        },
        false,
      )
    }

    if (this.props.list) {
      attrs.id = this.props.list.id
      attrs.recipients = this.props.list.recipients
      await this.props.updateEmailGroup(attrs)
      this.props.navigate(`/lists/${attrs.id}`)
    } else {
      await this.props.createEmailGroup(attrs)
      this.props.navigate(`/lists`)
    }
  }
  _handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    this.props.clearValidations()
    this.setState(prevState => ({
      ...prevState,
      [evt.target.name]: evt.target.value,
    }))
  }
  _handleClose = () => {
    this.props.navigate('/lists')
  }

  render() {
    const { formTitle, user, emailGroups } = this.props

    // the group can arrive from the full load or from a single-group fetch
    if (emailGroups.isFetching || emailGroups.isFetchingEmailGroup) {
      return <Spinner />
    }

    const valid = this.props.isValid()
    return (
      <div className="Form">
        <Heading size="600">{formTitle}</Heading>
        <Box mb="4">
          <TextField
            label="Mailing list name"
            name="name"
            value={this.state.name}
            invalid={!this.props.isValid('name')}
            error={this.props.getValidationMessages('name').toString()}
            onChange={this._handleChange}
            required
          />
        </Box>
        <Box mb="4">
          <TextField
            label="Sender display name"
            name="sender_name"
            placeholder={user.first_name}
            value={this.state.sender_name}
            invalid={!this.props.isValid('sender_name')}
            error={this.props.getValidationMessages('sender_name').toString()}
            onChange={this._handleChange}
            tooltipId="tooltip_campaigns_sender_name"
          />
        </Box>
        <Box mb="4">
          <TextField
            label="Sender from address"
            name="sender_email"
            placeholder={user.email}
            value={this.state.sender_email}
            invalid={!this.props.isValid('sender_email')}
            error={this.props.getValidationMessages('sender_email').toString()}
            onChange={this._handleChange}
            tooltipId="tooltip_campaigns_sender_email"
          />
        </Box>
        <Box mb="4">
          <Label
            caption="Business address"
            tooltipId="tooltip_campaigns_business_address"
            validationError={this.props
              .getValidationMessages('business_address_text')
              .toString()}
          />
          <BusinessAddressField
            invalid={!this.props.isValid('business_address_text')}
            initialContent={this.state.business_address}
            onChange={this._handleBusinessAddressChange}
          />
        </Box>

        {this.props.emailGroups.error ? (
          <div className="Login_ValidationMessage">Failed to save</div>
        ) : null}
        <div className="Form_ButtonsContainer separated-small">
          <ButtonContainer>
            <Button caption="Cancel" onClick={this._handleClose} />
            <Button
              appearance="primary"
              caption="Save"
              disabled={!valid}
              progressIndicator={this.props.emailGroups.isSaving}
              progressCaption="Saving"
              onClick={this._handleSubmit}
            />
          </ButtonContainer>
        </div>
      </div>
    )
  }
}

const ConnectedMailingListForm = connect(
  mapStateToProps,
  actions,
)(validation(strategy)(MailingListForm))

// Wrapper component to provide router params and navigation to the connected component
const MailingListFormWithRouter = (props: any) => {
  const navigate = useNavigate()
  const params = useParams<{ listId?: string }>()
  return (
    <ConnectedMailingListForm {...props} navigate={navigate} params={params} />
  )
}

export default MailingListFormWithRouter as any
